<template>
  <b-modal
    id="upload-modal"
    size="lg"
    cancel-title="Annulla"
    cancel-variant="outline-secondary"
    centered
    :ok-title="uploading ? 'Caricamento in corso...' : 'Carica'"
    :ok-disabled="!canSubmit"
    :cancel-disabled="uploading"
    :no-close-on-backdrop="uploading"
    :no-close-on-esc="uploading"
    :hide-header-close="uploading"
    :title="hasMultiple() ? 'Seleziona uno o più file' : 'Seleziona un file'"
    @ok="onOk"
    @show="onShow"
  >
    <!-- <b-form>
      <b-form-group>
        <label for="name">select / Drag&Drop file to upload:</label>
        <b-form-file v-model="files" size="lg" placeholder="select or Drag&Drop file"
          :multiple="uploadMethod !== 'copy'" />
      </b-form-group>
      <b-form-select v-if="uploadMethod === 'copy'" v-model="is_minor" :options="options"></b-form-select>
    </b-form> -->
    <div :class="{ 'upload-modal-locked': uploading }">
      <DropZone
        :key="dropZoneKey"
        :multiple="hasMultiple() ? true : false"
        @drop="getFiles"
      />
    </div>
    <small
      v-if="limitText"
      class="d-block text-muted mt-50"
    >{{ limitText }}</small>
    <b-alert
      variant="warning"
      class="mt-1 mb-0"
      :show="selectionCheck.blocked"
    >
      <div class="alert-body">
        {{ selectionCheck.message }}
        <ul
          v-if="selectionCheck.offenders.length > 0"
          class="mb-0 mt-50"
        >
          <li
            v-for="offender in selectionCheck.offenders"
            :key="offender"
          >
            {{ offender }}
          </li>
        </ul>
      </div>
    </b-alert>
    <b-form>
      <b-form-select
        v-if="uploadMethod === 'copy'"
        v-model="is_minor"
        :options="options"
      />
    </b-form>
    <div
      v-if="uploading"
      class="mt-1"
    >
      <b-progress
        :value="progress.barValue"
        :max="100"
        :show-progress="progress.determinate && progress.phase === 'uploading'"
        height="1.25rem"
        animated
        striped
      />
      <small class="d-block mt-50">{{ progressText }}</small>
    </div>
    <b-alert
      variant="danger"
      class="mt-1 mb-0"
      :show="Boolean(errorMessage)"
    >
      <div class="alert-body">
        {{ errorMessage }}
      </div>
    </b-alert>
  </b-modal>
</template>

<script>
import {
  BModal, BForm, BFormSelect, BProgress, BAlert,
} from 'bootstrap-vue'
import objectsRepository from '@/api/objectsRepository'
import useMyStore from '@/services/useMyStore'
import uploadLimits from '@/helpers/upload-limits'
import apiErrors from '@/helpers/api-errors'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import DropZone from '../DropZone.vue'

export default {
  name: 'Modal',
  components: {
    DropZone,
    BModal,
    BFormSelect,
    BForm,
    BProgress,
    BAlert,
  },
  setup() {
    const {
      currentObject,
      uploadMethod,
      currentFolder,
    } = useMyStore()
    return {
      currentObject,
      uploadMethod,
      currentFolder,
    }
  },
  data() {
    return {
      is_minor: 0,
      files: null,
      options: [
        {
          value: 0,
          text: 'Major Release',
        },
        {
          value: 1,
          text: 'Minor Release',
        },
      ],
      uploading: false,
      progress: uploadLimits.getProgress(),
      errorMessage: '',
      limit: null,
      dropZoneKey: 0,
    }
  },
  computed: {
    filesToUpload() {
      if (!Array.isArray(this.files)) {
        return []
      }
      return this.uploadMethod === 'copy' ? this.files.slice(0, 1) : this.files
    },
    limitText() {
      return this.limit ? `Dimensione massima per file: ${this.limit.human} (per file più grandi usa FTP)` : ''
    },
    selectionCheck() {
      return uploadLimits.checkSelection(this.filesToUpload, this.limit)
    },
    canSubmit() {
      return !this.uploading && this.filesToUpload.length > 0 && !this.selectionCheck.blocked
    },
    progressText() {
      return uploadLimits.progressLabel(this.progress)
    },
    selectionKey() {
      return this.filesToUpload.map(file => `${file.name}:${file.size}`).join('|')
    },
  },
  watch: {
    selectionKey() {
      this.errorMessage = ''
    },
  },
  methods: {
    getFiles(value) {
      this.files = value
    },
    onShow() {
      this.files = null
      this.dropZoneKey += 1
      this.errorMessage = ''
      this.progress = uploadLimits.getProgress()
      this.limit = null
      this.loadLimit()
    },
    async loadLimit() {
      try {
        this.limit = uploadLimits.parseUploadLimit(await objectsRepository.uploadLimit())
      } catch (error) {
        // Degrado silenzioso: senza limite noto non c'è pre-controllo
        this.limit = null
        apiErrors.logApiError(error)
      }
    },
    async onOk(bvModalEvent) {
      // Il modal non deve chiudersi da solo: lo chiudiamo a risposta ricevuta
      bvModalEvent.preventDefault()
      if (this.uploading) {
        return
      }
      await this.upload()
    },
    onUploadProgress(event) {
      this.progress = uploadLimits.getProgress(event)
    },
    async upload() {
      if (this.filesToUpload.length === 0) {
        this.showError('Seleziona almeno un file da caricare.')
        return
      }
      const check = uploadLimits.checkSelection(this.filesToUpload, this.limit)
      if (check.blocked) {
        this.showError(check.message)
        return
      }

      this.errorMessage = ''
      this.progress = uploadLimits.getProgress()
      this.uploading = true

      let failure = null
      try {
        const formData = new FormData()
        const config = { onUploadProgress: this.onUploadProgress }
        let response
        if (this.uploadMethod === 'new') {
          this.filesToUpload.forEach(file => {
            formData.append('file[]', file)
          })
          formData.append('father_id', this.currentFolder)
          response = await objectsRepository.create(formData, config)
        } else {
          formData.append('file', this.filesToUpload[0])
          formData.append('label', this.filesToUpload[0].name)
          formData.append('is_minor', this.is_minor)
          response = await objectsRepository.newVersion(this.currentObject, formData, config)
        }
        if (!(response && response.data && response.data.code)) {
          failure = response
        }
      } catch (error) {
        failure = error
      } finally {
        this.uploading = false
      }

      if (failure) {
        this.reportFailure(failure)
        return
      }

      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Successo!',
          text: this.filesToUpload.length > 1 ? 'File caricati.' : 'File caricato.',
          icon: 'CheckIcon',
          variant: 'success',
        },
      },
      {
        position: 'top-right',
      })
      this.$emit('uploaded')
      this.$bvModal.hide('upload-modal')
    },
    reportFailure(failure) {
      apiErrors.logApiError(failure)
      const result = uploadLimits.classifyUploadFailure(failure, {
        limit: this.limit,
        fileCount: this.filesToUpload.length,
      })
      this.showError(result.message)
    },
    showError(message) {
      this.errorMessage = message
      this.$toast({
        component: ToastificationContent,
        props: {
          title: 'Errore!',
          text: message,
          icon: 'XIcon',
          variant: 'danger',
        },
      },
      {
        position: 'top-center',
      })
    },
    close() {
      this.$emit('close')
    },
    hasMultiple() {
      return this.uploadMethod === 'new'
    },
  },
}
</script>

<style>
[dir="ltr"] .modal-header .close {
  margin: -0.5rem -0.6rem -0.5rem auto;
}

.upload-modal-locked {
  pointer-events: none;
  opacity: 0.6;
}
</style>
